// ─── src/components/FloatingUiManager.jsx ────────────────────────────────────
// Layout orchestrator for all fixed/floating surfaces.
//
// Owns ONLY:
//   z-index layering, safe area offsets, compare bar shifting,
//   mobile spacing, footer collision rules, bottom sheet positioning.
//
// Does NOT own:
//   messages, recommendations, intent, chat state, persistence, AI logic.
//   Those live in AuraCore (src/stores/auraCore.ts).
//
// Usage:
//   Wrap your app (inside AuraProvider) with <FloatingUiManager>.
//   Any floating surface reads offsets via useFloatingUi() or CSS variables.
//
//   <FloatingUiManager>
//     <AuraLauncher />
//     <AuraMiniBar />
//     <GlobalCompare />
//   </FloatingUiManager>
//
// CSS variables set on the root wrapper (readable anywhere in the subtree):
//   --fui-bottom-base        base bottom offset (desktop: 32px, mobile: safe-area + 16px)
//   --fui-bottom-aura        bottom for AuraLauncher + AuraMiniBar
//   --fui-bottom-compare     bottom for CompareBar (always 0)
//   --fui-compare-height     current CompareBar height (0 when closed)
//   --fui-safe-area-bottom   env(safe-area-inset-bottom, 0px)
//   --fui-z-aura-button      z-index for AuraLauncher FAB
//   --fui-z-aura-mini        z-index for AuraMiniBar / bottom sheet
//   --fui-z-compare          z-index for CompareBar
//   --fui-z-modal            z-index for FullChat modal

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// ─── Z-index registry ─────────────────────────────────────────────────────────
// Centralised here so every floating surface uses the same budget.
// Matches the comment block in AuraLauncher.jsx, single source of truth now.
export const Z = {
  compareBar:  850,
  auraButton:  1000,
  auraMini:    1002,
  tooltip:     1001,
  modal:       1010,
  dialog:      99999,
};

// ─── Layout constants ─────────────────────────────────────────────────────────
const COMPARE_BAR_HEIGHT_DEFAULT = 72; // px, updated via measurement if available
const DESKTOP_BASE               = 32; // px from viewport bottom when nothing is active

// ─── Context ──────────────────────────────────────────────────────────────────

const FloatingUiCtx = createContext(null);

/**
 * useFloatingUi()
 * Returns layout state for floating surfaces.
 * Safe to call in any component mounted inside <FloatingUiManager>.
 */
export function useFloatingUi() {
  const ctx = useContext(FloatingUiCtx);
  if (!ctx) throw new Error('useFloatingUi must be used inside FloatingUiManager');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function FloatingUiManager({ children }) {
  const [compareBarOpen,   setCompareBarOpen]   = useState(false);
  const [compareBarHeight, setCompareBarHeight] = useState(0);
  const [editorialMode,    setEditorialMode]    = useState(false);
  const [isMobile,         setIsMobile]         = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  const wrapperRef = useRef(null);

  // ── Detect mobile / tablet ─────────────────────────────────────────────────
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', update, { passive: true });
    update();
    return () => window.removeEventListener('resize', update);
  }, []);

  // ── Listen: compare bar open/close ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const active = !!e.detail?.active;
      setCompareBarOpen(active);
      // Use measured height if provided by the event, else fall back to default
      if (active && e.detail?.height) {
        setCompareBarHeight(e.detail.height);
      } else if (active) {
        setCompareBarHeight(COMPARE_BAR_HEIGHT_DEFAULT);
      } else {
        setCompareBarHeight(0);
      }
    };
    window.addEventListener('lwd:compare-bar', handler);
    return () => window.removeEventListener('lwd:compare-bar', handler);
  }, []);

  // ── Listen: editorial mode ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => setEditorialMode(!!e.detail?.active);
    window.addEventListener('lwd:editorial-mode', handler);
    return () => window.removeEventListener('lwd:editorial-mode', handler);
  }, []);

  // ── Derived offsets ────────────────────────────────────────────────────────
  // auraBottom: how far from the viewport bottom the Aura launcher/mini sits.
  // On desktop: base + compare bar height when open.
  // On mobile:  safe-area-inset-bottom is handled via CSS variable; we add
  //             the compare bar bump on top.
  const auraBottomDesktop = compareBarOpen
    ? DESKTOP_BASE + compareBarHeight + 8  // 8px gap above compare bar
    : DESKTOP_BASE;

  // ── Sync CSS variables onto wrapper ───────────────────────────────────────
  // Components can read these without subscribing to React state.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.setProperty('--fui-compare-height',   `${compareBarHeight}px`);
    el.style.setProperty('--fui-bottom-aura',       `${auraBottomDesktop}px`);
    el.style.setProperty('--fui-z-aura-button',     String(Z.auraButton));
    el.style.setProperty('--fui-z-aura-mini',       String(Z.auraMini));
    el.style.setProperty('--fui-z-compare',         String(Z.compareBar));
    el.style.setProperty('--fui-z-modal',           String(Z.modal));
  }, [compareBarHeight, auraBottomDesktop]);

  // ── Context value (stable reference) ──────────────────────────────────────
  const ctx = {
    compareBarOpen,
    compareBarHeight,
    editorialMode,
    isMobile,
    auraBottomDesktop,
    // Mobile bottom: pure CSS expression, components read --fui-bottom-aura
    // but the mobile value needs safe-area, so we give them the full expression.
    auraBottomMobile: compareBarOpen
      ? `calc(env(safe-area-inset-bottom, 0px) + ${compareBarHeight + 16 + 84}px)`
      : `calc(env(safe-area-inset-bottom, 0px) + 84px)`,
    // Bottom sheet positioning: always full-width, anchored to bottom
    bottomSheetStyle: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: Z.auraMini,
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    },
    Z,
  };

  return (
    <FloatingUiCtx.Provider value={ctx}>
      <div
        ref={wrapperRef}
        data-fui
        style={{
          // Not a layout container, zero size, just a variable scope root
          '--fui-safe-area-bottom': 'env(safe-area-inset-bottom, 0px)',
          '--fui-bottom-base':      `${DESKTOP_BASE}px`,
          '--fui-bottom-compare':   '0px',
          '--fui-z-dialog':         String(Z.dialog),
        }}
      >
        {children}
      </div>
    </FloatingUiCtx.Provider>
  );
}
